import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Filler,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import config from './config';

// ---------------------------------------------------------------------------
// Bay Inspection TV Dashboard
//
// Wall-mounted TV view of the current In Progress inspection in a given
// work bay. Auth re-uses TVDashboardToken from Django admin. One token can
// power every bay TV; the bay is selected per-device via the URL.
//
//     /bay-dashboard?token=<TOKEN>&bay=<BAY_ID_OR_NAME>
//
// Background = faded burndown chart (chart.js Line) so passers-by can see
// trajectory at a glance. Foreground panels overlay it.
// ---------------------------------------------------------------------------

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend);

const REFRESH_INTERVAL_MS = 30 * 1000;
const MAX_VISIBLE_CARDS = 6;
// Cap the notes feed at a handful on the TV — too many notes makes the
// row text tiny and unreadable across the room. Overflow is signalled
// with a "+N more" footer instead.
const MAX_VISIBLE_NOTES = 5;

// Theme tokens. Status / progress colors (success/warn/danger/accent) stay
// the same in both themes so the chart series and progress bars keep
// signalling the same thing.
const getColors = (theme = 'dark') => {
    const isDark = theme === 'dark';
    return {
        bg: isDark ? '#0b0d10' : '#f4f6f8',
        panel: isDark ? 'rgba(22, 26, 32, 0.78)' : 'rgba(255, 255, 255, 0.92)',
        panelBorder: isDark ? '#2c3641' : '#cdd5dd',
        text: isDark ? '#f0f3f6' : '#1f2933',
        muted: isDark ? '#9aa6b2' : '#5a6470',
        accent: '#4f9eff',
        success: '#28a745',
        warn: '#ffc107',
        danger: '#dc3545',
        track: isDark ? '#1f2933' : '#dee2e6',
        toggleBg: isDark ? '#1f2933' : '#ffffff',
        toggleBorder: isDark ? '#2c3641' : '#cdd5dd',
        toggleText: isDark ? '#f0f3f6' : '#1f2933',
        chartGrid: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        chartTick: isDark ? '#9aa6b2' : '#5a6470',
        chartLegend: isDark ? '#f0f3f6' : '#1f2933',
        idealLine: isDark ? 'rgba(120, 144, 168, 0.9)' : 'rgba(74, 90, 110, 0.85)',
    };
};

const getStyles = (c) => ({
    page: {
        position: 'relative',
        backgroundColor: c.bg,
        color: c.text,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: 'sans-serif',
    },
    content: {
        position: 'relative',
        zIndex: 1,
        height: '100vh',
        width: '100vw',
        boxSizing: 'border-box',
        padding: '24px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flex: '0 0 auto',
    },
    bay: { fontSize: '42px', fontWeight: 800, margin: 0, letterSpacing: '1px' },
    bayMeta: { fontSize: '20px', color: c.muted, marginTop: '4px' },
    headerRight: { display: 'flex', alignItems: 'center', gap: '20px' },
    clock: { fontSize: '32px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
    liveBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: 700,
        padding: '4px 12px',
        borderRadius: '999px',
    },
    liveDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        animation: 'tvDashPulse 2s infinite',
    },
    themeToggle: {
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 700,
        padding: '6px 14px',
        borderRadius: '999px',
        backgroundColor: c.toggleBg,
        color: c.toggleText,
        border: `1px solid ${c.toggleBorder}`,
        fontFamily: 'inherit',
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 14px',
        borderRadius: '6px',
        fontSize: '18px',
        fontWeight: 700,
        backgroundColor: c.warn,
        color: '#000',
        marginLeft: '10px',
        verticalAlign: 'middle',
    },
    panel: {
        backgroundColor: c.panel,
        border: `1px solid ${c.panelBorder}`,
        borderRadius: '12px',
        padding: '20px 24px',
    },
    mainRow: {
        display: 'grid',
        gridTemplateColumns: '320px 1fr',
        gap: '20px',
        flex: '0 0 auto',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
    },
    statBlock: { textAlign: 'center' },
    statValue: { fontSize: '46px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
    statSub: { fontSize: '18px', color: c.muted, marginTop: '4px' },
    statLabel: { fontSize: '14px', color: c.muted, marginTop: '8px', letterSpacing: '0.5px' },
    donutWrap: {
        width: '280px',
        height: '280px',
        position: 'relative',
        margin: '0 auto',
    },
    donutLabel: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    donutPct: { fontSize: '64px', fontWeight: 800, lineHeight: 1 },
    donutSub: { fontSize: '18px', color: c.muted, marginTop: '6px' },
    cardsPanel: { flex: '1 1 auto', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    cardsTitle: {
        fontSize: '20px',
        fontWeight: 700,
        marginBottom: '14px',
        letterSpacing: '0.5px',
        color: c.muted,
    },
    cardRow: {
        // 5 cols: work area | work center | progress bar | % | counts
        display: 'grid',
        gridTemplateColumns: '220px 140px 1fr 80px 130px',
        alignItems: 'center',
        gap: '14px',
        padding: '8px 0',
        borderBottom: `1px solid ${c.panelBorder}`,
    },
    cardRowLast: { borderBottom: 'none' },
    cardName: {
        fontWeight: 700,
        fontSize: '20px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    cardNameFallback: {
        fontWeight: 700,
        fontSize: '18px',
        color: c.muted,
        fontStyle: 'italic',
    },
    cardWorkCenter: {
        fontSize: '15px',
        color: c.muted,
        letterSpacing: '0.5px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    cardBar: { height: '14px', borderRadius: '7px', backgroundColor: c.track, overflow: 'hidden' },
    cardBarFill: { height: '100%', borderRadius: '7px' },
    cardPct: { fontSize: '16px', fontWeight: 700, textAlign: 'right' },
    cardRemaining: { fontSize: '14px', color: c.muted, textAlign: 'right' },

    // ---- Step notes panel --------------------------------------------------
    // Capped-height panel between TOP OPEN CARDS and the burndown. Lists
    // any non-empty notes attached to inspection items so the floor can
    // surface blockers / status updates to the room TV.
    notesPanel: {
        flex: '0 1 auto',
        minHeight: 0,
        maxHeight: '30vh',
        display: 'flex',
        flexDirection: 'column',
    },
    notesList: {
        flex: '1 1 auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    noteRow: {
        display: 'grid',
        gridTemplateColumns: '170px 90px 1fr',
        alignItems: 'start',
        gap: '14px',
        padding: '8px 0',
        borderBottom: `1px solid ${c.panelBorder}`,
    },
    noteRowLast: { borderBottom: 'none' },
    noteContext: {
        fontWeight: 700,
        fontSize: '16px',
        color: c.text,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    noteContextSub: {
        fontSize: '12px',
        color: c.muted,
        letterSpacing: '0.5px',
        marginTop: '2px',
    },
    noteStep: {
        fontSize: '14px',
        color: c.muted,
        fontVariantNumeric: 'tabular-nums',
    },
    noteText: {
        fontSize: '16px',
        color: c.text,
        lineHeight: 1.35,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    notesMore: {
        marginTop: '8px',
        fontSize: '13px',
        color: c.muted,
        letterSpacing: '0.5px',
        textAlign: 'center',
    },
    // ---- Inspection-level "main" note --------------------------------------
    // Rendered as a distinct row at the very top of the NOTES panel so the
    // room can see overall status (e.g. "awaiting parts") without scanning
    // through individual step notes.
    mainNoteRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '10px 12px',
        marginBottom: '10px',
        backgroundColor: c.track,
        border: `1px solid ${c.panelBorder}`,
        borderLeft: `4px solid ${c.accent}`,
        borderRadius: '6px',
    },
    mainNoteBadge: {
        display: 'inline-block',
        fontSize: '12px',
        fontWeight: 800,
        letterSpacing: '1px',
        padding: '3px 10px',
        borderRadius: '4px',
        backgroundColor: c.accent,
        color: '#fff',
        flex: '0 0 auto',
    },
    mainNoteText: {
        fontSize: '17px',
        fontWeight: 600,
        color: c.text,
        lineHeight: 1.4,
        flex: '1 1 auto',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
    },
    noteStatusOpen: {
        display: 'inline-block',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        padding: '2px 6px',
        borderRadius: '3px',
        backgroundColor: c.warn,
        color: '#000',
        marginRight: '6px',
        verticalAlign: '2px',
    },
    noteStatusDone: {
        display: 'inline-block',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        padding: '2px 6px',
        borderRadius: '3px',
        backgroundColor: c.success,
        color: '#fff',
        marginRight: '6px',
        verticalAlign: '2px',
    },
    fatalBox: {
        position: 'relative',
        zIndex: 1,
        maxWidth: '720px',
        margin: '15vh auto',
        backgroundColor: c.panel,
        border: `1px solid ${c.panelBorder}`,
        borderRadius: '12px',
        padding: '36px',
        textAlign: 'center',
    },
    fatalTitle: { fontSize: '34px', fontWeight: 800, marginBottom: '16px' },
    fatalBody: { fontSize: '20px', color: c.muted, lineHeight: 1.6 },
    code: {
        fontFamily: 'monospace',
        backgroundColor: c.bg,
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '17px',
    },
    idleStack: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
});


const pulseKeyframes = `
@keyframes tvDashPulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
    100% { opacity: 1; transform: scale(1); }
}`;

if (typeof document !== 'undefined' && !document.getElementById('bay-dashboard-keyframes')) {
    const style = document.createElement('style');
    style.id = 'bay-dashboard-keyframes';
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);
}

function getApiBaseURL() {
    const envBase = process.env.REACT_APP_API_BASE_URL || config.API_BASE_URL || '';
    let base = (envBase || '').trim().replace(/\/+$/, '');
    if (!base) return '/api';
    if (!/\/(api)$/.test(base)) base += '/api';
    return base;
}

const isoDateOnly = (s) => (s ? String(s).split('T')[0] : null);

const formatShortDate = (s) => {
    if (!s) return 'N/A';
    try {
        const [y, m, d] = isoDateOnly(s).split('-').map((n) => parseInt(n, 10));
        return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    } catch {
        return 'N/A';
    }
};

const businessDaysBetween = (fromIso, toIso) => {
    if (!fromIso || !toIso) return null;
    const a = new Date(isoDateOnly(fromIso));
    const b = new Date(isoDateOnly(toIso));
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    let days = 0;
    const cursor = new Date(a);
    while (cursor < b) {
        cursor.setDate(cursor.getDate() + 1);
        if (cursor.getDay() !== 0 && cursor.getDay() !== 6) days += 1;
    }
    return days;
};

// Build chart.js data from the burndown payload using the same logic as
// BurndownChartPage.js — weekday labels start_date..ecd, ideal line linear
// from total to 0, actual line forward-filled.
function useBurndownChartData(inspection, colors) {
    return useMemo(() => {
        const bd = inspection?.burndown;
        if (!bd || !bd.start_date || !bd.ecd) return null;

        const labels = [];
        const startDate = new Date(`${bd.start_date}T00:00:00`);
        const endDate = new Date(`${bd.ecd}T00:00:00`);
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

        const cursor = new Date(startDate);
        while (cursor <= endDate) {
            if (cursor.getDay() !== 0 && cursor.getDay() !== 6) {
                labels.push(cursor.toISOString().split('T')[0]);
            }
            cursor.setDate(cursor.getDate() + 1);
        }
        if (labels.length === 0) return null;

        const total = bd.total_items || 0;
        const ideal = labels.map((_, i) => Math.max(0, Math.round(total - total * (i / Math.max(1, labels.length - 1)))));

        const progressByDate = {};
        for (const row of bd.daily_progress || []) {
            if (row?.date) progressByDate[isoDateOnly(row.date)] = row.tasks_remaining;
        }
        const todayIso = new Date().toISOString().split('T')[0];
        let lastKnown = total;
        const actual = labels.map((d) => {
            if (d in progressByDate) {
                lastKnown = progressByDate[d];
                return lastKnown;
            }
            return d <= todayIso ? lastKnown : null;
        });

        return {
            labels: labels.map(formatShortDate),
            datasets: [
                {
                    label: 'Ideal',
                    data: ideal,
                    borderColor: colors.idealLine,
                    borderDash: [6, 6],
                    pointRadius: 0,
                    borderWidth: 2,
                    tension: 0,
                },
                {
                    label: 'Actual',
                    data: actual,
                    borderColor: 'rgba(79, 158, 255, 1)',
                    backgroundColor: 'rgba(79, 158, 255, 0.25)',
                    fill: true,
                    pointRadius: 0,
                    borderWidth: 3,
                    tension: 0.15,
                    spanGaps: false,
                },
            ],
        };
    }, [inspection, colors]);
}

// Foreground chart shown in its own panel under the cards list
const getForegroundChartOptions = (c) => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
        legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
                color: c.chartLegend,
                font: { size: 14, weight: 'bold' },
                boxWidth: 18,
                boxHeight: 2,
                padding: 16,
            },
        },
        tooltip: { enabled: false },
    },
    scales: {
        x: {
            grid: { color: c.chartGrid },
            ticks: { color: c.chartTick, font: { size: 12 }, maxRotation: 0, autoSkipPadding: 16 },
        },
        y: {
            beginAtZero: true,
            grid: { color: c.chartGrid },
            ticks: { color: c.chartTick, font: { size: 12 } },
            title: { display: true, text: 'Tasks Remaining', color: c.chartTick, font: { size: 12 } },
        },
    },
});

// Circular donut % indicator (SVG, no external dep)
function DonutProgress({ percent, colors, styles }) {
    const size = 280;
    const stroke = 22;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const pct = Math.max(0, Math.min(100, Number.isFinite(percent) ? percent : 0));
    const dash = (pct / 100) * circumference;
    const ringColor = pct >= 80 ? colors.success : pct >= 50 ? colors.accent : pct >= 25 ? colors.warn : colors.danger;
    return (
        <div style={styles.donutWrap}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={radius} stroke={colors.track} strokeWidth={stroke} fill="none" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={ringColor}
                    strokeWidth={stroke}
                    fill="none"
                    strokeDasharray={`${dash} ${circumference}`}
                    strokeLinecap="round"
                />
            </svg>
            <div style={styles.donutLabel}>
                <div style={styles.donutPct}>{Math.round(pct)}%</div>
                <div style={styles.donutSub}>COMPLETE</div>
            </div>
        </div>
    );
}

function BayDashboardPage() {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const token = params.get('token');
    const bayParam = params.get('bay');

    const [data, setData] = useState({ device_name: '', bay: null, inspection: null, generated_at: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [now, setNow] = useState(new Date());
    const failuresRef = useRef(0);

    // Theme (persisted per-browser).
    const [theme, setTheme] = useState(() => {
        if (typeof window === 'undefined') return 'dark';
        return window.localStorage.getItem('bay-dashboard-theme') || 'dark';
    });
    const toggleTheme = useCallback(() => {
        setTheme((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            try { window.localStorage.setItem('bay-dashboard-theme', next); } catch { /* ignore */ }
            return next;
        });
    }, []);
    const colors = useMemo(() => getColors(theme), [theme]);
    const styles = useMemo(() => getStyles(colors), [colors]);
    const foregroundChartOptions = useMemo(() => getForegroundChartOptions(colors), [colors]);

    const fetchData = useCallback(async () => {
        if (!token || !bayParam) return;
        try {
            const response = await axios.get(
                `${getApiBaseURL()}/tv-dashboard/bay-inspection/`,
                { params: { token, bay: bayParam }, withCredentials: false },
            );
            setData(response.data);
            setError(null);
            failuresRef.current = 0;
        } catch (err) {
            failuresRef.current += 1;
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                setError({ kind: 'auth', message: 'Token rejected. Check the URL or generate a new token in Django admin.' });
            } else if (status === 404) {
                setError({ kind: 'bay-not-found', message: `Bay '${bayParam}' not found.` });
            } else if (failuresRef.current >= 3) {
                setError({ kind: 'network', message: 'Lost connection to the server. Retrying…' });
            }
        } finally {
            setLoading(false);
        }
    }, [token, bayParam]);

    useEffect(() => {
        if (!token || !bayParam) {
            setError({ kind: 'missing-args', message: 'Missing token or bay.' });
            setLoading(false);
            return undefined;
        }
        fetchData();
        const id = setInterval(fetchData, REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [token, bayParam, fetchData]);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const inspection = data.inspection;
    const chartData = useBurndownChartData(inspection, colors);

    if (!token || !bayParam) {
        return (
            <div style={styles.page}>
                <div style={styles.fatalBox}>
                    <div style={styles.fatalTitle}>Bay Inspection Dashboard</div>
                    <div style={styles.fatalBody}>
                        Missing required parameters. Use:<br />
                        <span style={styles.code}>/bay-dashboard?token=&lt;TOKEN&gt;&bay=&lt;BAY_ID_OR_NAME&gt;</span>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.fatalBox}>
                    <div style={styles.fatalTitle}>Loading bay…</div>
                </div>
            </div>
        );
    }

    if (error && (error.kind === 'auth' || error.kind === 'bay-not-found')) {
        return (
            <div style={styles.page}>
                <div style={styles.fatalBox}>
                    <div style={styles.fatalTitle}>{error.kind === 'auth' ? 'Token rejected' : 'Bay not found'}</div>
                    <div style={styles.fatalBody}>{error.message}</div>
                </div>
            </div>
        );
    }

    const bayName = data.bay?.name || `Bay ${bayParam}`;
    const liveOk = !error;

    // Empty state: no In Progress inspection in this bay
    if (!inspection) {
        return (
            <div style={styles.page}>
                <div style={styles.content}>
                    <div style={styles.header}>
                        <div>
                            <h1 style={styles.bay}>{bayName}</h1>
                            <div style={styles.bayMeta}>No active inspection</div>
                        </div>
                        <div style={styles.headerRight}>
                            <div
                                style={{
                                    ...styles.liveBadge,
                                    backgroundColor: liveOk ? 'rgba(40,167,69,0.15)' : 'rgba(220,53,69,0.15)',
                                    color: liveOk ? colors.success : colors.danger,
                                    border: `1px solid ${liveOk ? colors.success : colors.danger}`,
                                }}
                            >
                                <span style={{ ...styles.liveDot, backgroundColor: liveOk ? colors.success : colors.danger }} />
                                {liveOk ? 'LIVE' : 'OFFLINE'}
                            </div>
                            <button
                                type="button"
                                onClick={toggleTheme}
                                style={styles.themeToggle}
                                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                            >
                                {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                            </button>
                            <div style={styles.clock}>{now.toLocaleTimeString()}</div>
                        </div>
                    </div>
                    <div style={{ ...styles.panel, flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={styles.idleStack}>
                            <div style={{ fontSize: '32px', color: colors.muted }}>Bay is open</div>
                            <div style={{ fontSize: '18px', color: colors.muted }}>
                                Waiting for an inspection to be started in this bay.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const visibleCards = (inspection.cards || [])
        // Hide already-completed cards from the open list — saves screen real estate
        .filter((c) => c.outstanding_items > 0)
        .sort((a, b) => b.outstanding_items - a.outstanding_items)
        .slice(0, MAX_VISIBLE_CARDS);

    const allNotes = inspection.step_notes || [];
    const visibleNotes = allNotes.slice(0, MAX_VISIBLE_NOTES);
    const extraNotes = Math.max(0, allNotes.length - visibleNotes.length);
    const mainNote = (inspection.notes || '').trim();
    const hasAnyNotes = !!mainNote || allNotes.length > 0;

    const remainingItems = (inspection.total_items || 0) - (inspection.completed_items || 0);
    const daysLeft = businessDaysBetween(new Date().toISOString(), inspection.ecd);

    return (
        <div style={styles.page}>
            <div style={styles.content}>
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.bay}>
                            {bayName}
                            <span style={styles.statusBadge}>{inspection.status}</span>
                        </h1>
                        <div style={styles.bayMeta}>
                            BUNO <strong>{inspection.buno}</strong>
                            {inspection.tms ? ` · ${inspection.tms}` : ''}
                            {inspection.site ? ` · ${inspection.site}` : ''}
                            {inspection.checklist_type ? ` · ${inspection.checklist_type}` : ''}
                        </div>
                    </div>
                    <div style={styles.headerRight}>
                        <div
                            style={{
                                ...styles.liveBadge,
                                backgroundColor: liveOk ? 'rgba(40,167,69,0.15)' : 'rgba(220,53,69,0.15)',
                                color: liveOk ? colors.success : colors.danger,
                                border: `1px solid ${liveOk ? colors.success : colors.danger}`,
                            }}
                        >
                            <span style={{ ...styles.liveDot, backgroundColor: liveOk ? colors.success : colors.danger }} />
                            {liveOk ? 'LIVE' : 'OFFLINE'}
                        </div>
                        <button
                            type="button"
                            onClick={toggleTheme}
                            style={styles.themeToggle}
                            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                        >
                            {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                        </button>
                        <div style={styles.clock}>{now.toLocaleTimeString()}</div>
                    </div>
                </div>

                <div style={styles.mainRow}>
                    <div style={styles.panel}>
                        <DonutProgress percent={inspection.percent_complete} colors={colors} styles={styles} />
                    </div>
                    <div style={{ ...styles.panel, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={styles.statsGrid}>
                            <div style={styles.statBlock}>
                                <div style={styles.statValue}>
                                    {inspection.completed_items}/{inspection.total_items}
                                </div>
                                <div style={styles.statSub}>{remainingItems} left</div>
                                <div style={styles.statLabel}>ITEMS</div>
                            </div>
                            <div style={styles.statBlock}>
                                <div style={styles.statValue}>
                                    {inspection.completed_cards}/{inspection.total_cards}
                                </div>
                                <div style={styles.statSub}>
                                    {(inspection.total_cards || 0) - (inspection.completed_cards || 0)} left
                                </div>
                                <div style={styles.statLabel}>CARDS</div>
                            </div>
                            <div style={styles.statBlock}>
                                <div style={{
                                    ...styles.statValue,
                                    color: daysLeft !== null && daysLeft <= 1 ? colors.danger
                                        : daysLeft !== null && daysLeft <= 3 ? colors.warn
                                        : colors.text,
                                }}>
                                    {daysLeft === null ? '—' : daysLeft}
                                </div>
                                <div style={styles.statSub}>workdays</div>
                                <div style={styles.statLabel}>UNTIL ECD</div>
                            </div>
                            <div style={styles.statBlock}>
                                <div style={{ ...styles.statValue, fontSize: '28px' }}>{formatShortDate(inspection.start_date)}</div>
                                <div style={styles.statSub}>→ {formatShortDate(inspection.ecd)}</div>
                                <div style={styles.statLabel}>START → ECD</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ ...styles.panel, ...styles.cardsPanel, flex: '1 1 0' }}>
                    <div style={styles.cardsTitle}>
                        TOP OPEN CARDS
                        {visibleCards.length > 0
                            ? ` · ${visibleCards.length} of ${(inspection.cards || []).filter((c) => c.outstanding_items > 0).length} shown`
                            : ' · all cards complete'}
                    </div>
                    <div style={{ flex: '1 1 auto', overflow: 'hidden' }}>
                        {visibleCards.length === 0 ? (
                            <div style={{ textAlign: 'center', color: colors.muted, padding: '24px', fontSize: '20px' }}>
                                All cards complete — ready to close out.
                            </div>
                        ) : (
                            visibleCards.map((card, i) => {
                                const pct = card.percent_complete || 0;
                                const fillColor = pct >= 80 ? colors.success : pct >= 50 ? colors.accent : pct >= 25 ? colors.warn : colors.danger;
                                const rowStyle = i === visibleCards.length - 1
                                    ? { ...styles.cardRow, ...styles.cardRowLast }
                                    : styles.cardRow;
                                const workArea = (card.work_area || '').trim();
                                const workCenter = (card.work_center || '').trim();
                                return (
                                    <div key={card.card} style={rowStyle}>
                                        <div
                                            style={workArea ? styles.cardName : styles.cardNameFallback}
                                            title={workArea ? `${workArea} · Card ${card.card}` : `Card ${card.card}`}
                                        >
                                            {workArea || `Card ${card.card}`}
                                        </div>
                                        <div style={styles.cardWorkCenter} title={workCenter ? `Work Center ${workCenter}` : ''}>
                                            {workCenter ? `WC ${workCenter}` : '—'}
                                        </div>
                                        <div style={styles.cardBar}>
                                            <div style={{ ...styles.cardBarFill, width: `${pct}%`, backgroundColor: fillColor }} />
                                        </div>
                                        <div style={styles.cardPct}>{pct}%</div>
                                        <div style={styles.cardRemaining}>
                                            {card.completed_items}/{card.total_items} · {card.outstanding_items} left
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {hasAnyNotes && (
                    <div style={{ ...styles.panel, ...styles.notesPanel }}>
                        <div style={styles.cardsTitle}>
                            NOTES
                            {mainNote ? ' · MAIN' : ''}
                            {allNotes.length > 0 ? ` · ${allNotes.length} step note${allNotes.length === 1 ? '' : 's'}` : ''}
                        </div>
                        <div style={styles.notesList}>
                            {mainNote && (
                                <div style={styles.mainNoteRow}>
                                    <span style={styles.mainNoteBadge}>MAIN</span>
                                    <div style={styles.mainNoteText} title={mainNote}>{mainNote}</div>
                                </div>
                            )}
                            {visibleNotes.map((n, i) => {
                                const rowStyle = i === visibleNotes.length - 1
                                    ? { ...styles.noteRow, ...styles.noteRowLast }
                                    : styles.noteRow;
                                const isOpen = n.status !== 'Completed';
                                const cardLabel = n.work_area || (n.card ? `Card ${n.card}` : 'Item');
                                const wc = (n.work_center || '').trim();
                                return (
                                    <div key={`${n.card}-${n.step}-${i}`} style={rowStyle}>
                                        <div>
                                            <div style={styles.noteContext} title={`${cardLabel}${n.card ? ` · Card ${n.card}` : ''}`}>
                                                {cardLabel}
                                            </div>
                                            {(n.card || wc) && (
                                                <div style={styles.noteContextSub}>
                                                    {n.card ? `CARD ${n.card}` : ''}
                                                    {n.card && wc ? ' · ' : ''}
                                                    {wc ? `WC ${wc}` : ''}
                                                </div>
                                            )}
                                        </div>
                                        <div style={styles.noteStep}>
                                            {n.step ? `Step ${n.step}` : ''}
                                        </div>
                                        <div style={styles.noteText} title={n.note}>
                                            <span style={isOpen ? styles.noteStatusOpen : styles.noteStatusDone}>
                                                {isOpen ? 'OPEN' : 'DONE'}
                                            </span>
                                            {n.note}
                                        </div>
                                    </div>
                                );
                            })}
                            {extraNotes > 0 && (
                                <div style={styles.notesMore}>+ {extraNotes} more note{extraNotes === 1 ? '' : 's'}…</div>
                            )}
                        </div>
                    </div>
                )}

                {chartData && (
                    <div style={{ ...styles.panel, flex: '1 1 0', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <div style={styles.cardsTitle}>BURNDOWN · TASKS REMAINING</div>
                        <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                            <Line data={chartData} options={foregroundChartOptions} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BayDashboardPage;
